import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Modal, InputNumber, Checkbox, DatePicker, message } from 'antd';
import {
  convertLocalTimeToEpoch,
  dateTimeFormat
} from '../../../../util/timezone';

const { RangePicker } = DatePicker;

const PERCENTILE_OPTIONS = [
  { label: 'p50 (50th percentile)', value: 0.5 },
  { label: 'p75 (75th percentile)', value: 0.75 },
  { label: 'p90 (90th percentile)', value: 0.9 },
  { label: 'p95 (95th percentile)', value: 0.95 },
  { label: 'p99 (99th percentile)', value: 0.99 }
];

const ALL_PERCENTILE_VALUES = PERCENTILE_OPTIONS.map((opt) => opt.value);

// Method families. The Python analyzer resolves each to an upper-tail or
// lower-tail variant per rule operator (e.g. mean_sd → μ+σ for >=, μ−σ for <=).
const METHOD_OPTIONS = [
  { label: 'Mean (μ)', value: 'mean' },
  { label: 'Mean ± SD (μ ± σ)', value: 'mean_sd' },
  { label: 'Mean ± 2SD (μ ± 2σ)', value: 'mean_2sd' },
  { label: 'Mean-Max / Mean-Min ((μ + max | min) / 2)', value: 'mean_extremum' }
];

const disabledDate = (current) => current && current > moment().endOf('day');

/** The first reason the Generate config cannot be sent, or null when it can. */
export const generateConfigError = ({
  dateRange,
  targetAlertRate,
  percentiles,
  methods
}) => {
  if (!dateRange || !dateRange[0] || !dateRange[1]) {
    return 'Please select a date range.';
  }
  // FIN-13987 — without a target the analyser cannot rank the combinations, so the results
  // panel has no "closest to target" and lists them unranked.
  if (targetAlertRate == null) {
    return 'Please enter a target alert rate.';
  }
  if (
    (!percentiles || percentiles.length === 0) &&
    (!methods || methods.length === 0)
  ) {
    return 'Please select at least one percentile or computation method.';
  }
  return null;
};

const AutoThresholdConfigModal = ({
  visible,
  onCancel,
  onConfirm,
  isLoading
}) => {
  const [targetAlertRate, setTargetAlertRate] = useState(null);
  const [percentiles, setPercentiles] = useState(ALL_PERCENTILE_VALUES);
  const [methods, setMethods] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  const handleOpen = () => {
    setTargetAlertRate(null);
    setPercentiles(ALL_PERCENTILE_VALUES);
    setMethods([]);
    setDateRange(null);
  };

  const handleConfirm = () => {
    const error = generateConfigError({
      dateRange,
      targetAlertRate,
      percentiles,
      methods
    });
    if (error) {
      message.warning(error);
      return;
    }

    const [from, to] = dateRange;
    const fromEpoch = convertLocalTimeToEpoch(
      from.clone().startOf('day').format(dateTimeFormat)
    );
    const toEpoch = convertLocalTimeToEpoch(
      to.clone().endOf('day').format(dateTimeFormat)
    );

    const config = {
      targetAlertRate: targetAlertRate / 100,
      percentiles,
      methods,
      dateRange: { from: fromEpoch, to: toEpoch }
    };

    onConfirm(config);
  };

  return (
    <Modal
      title="Auto-Threshold Configuration"
      visible={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Generate"
      confirmLoading={isLoading}
      afterClose={handleOpen}
      destroyOnClose>
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          Dataset Date Range <span style={{ color: 'red' }}>*</span>
        </div>
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          disabledDate={disabledDate}
          style={{ width: '100%' }}
        />
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 4 }}>
          Date range used for both training and prediction windows during
          feature extraction.
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          Target Alert Rate <span style={{ color: 'red' }}>*</span>
        </div>
        <InputNumber
          min={0}
          max={100}
          precision={1}
          value={targetAlertRate}
          onChange={setTargetAlertRate}
          placeholder="e.g. 2"
          style={{ width: '100%' }}
          addonAfter="%"
        />
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 4 }}>
          Share of analysed transactions you aim to alert on (0-100%). Threshold
          combinations are ranked by how close they come to it.
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Percentiles</div>
        <Checkbox.Group
          options={PERCENTILE_OPTIONS}
          value={percentiles}
          onChange={setPercentiles}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 4 }}>
          Select which percentile thresholds to compute.
        </div>
      </div>

      <div>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          Computation Methods
        </div>
        <Checkbox.Group
          options={METHOD_OPTIONS}
          value={methods}
          onChange={setMethods}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />
        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 4 }}>
          Statistical candidates in addition to percentiles. Direction is
          applied automatically (upper-tail for ≥/&gt;, lower-tail for ≤/&lt;).
        </div>
      </div>
    </Modal>
  );
};

AutoThresholdConfigModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

AutoThresholdConfigModal.defaultProps = {
  isLoading: false
};

export default AutoThresholdConfigModal;
